/**
 * SegmentedControl — the "select view" toggle. A 1px-hairline pill (radius 4px,
 * 2px inner padding, 2px gap) holding the option buttons (radius 2px). Selected
 * = blue text/icon on a blue-subtle fill with a blue border; resting = subtle
 * icon and a transparent border (kept so size never shifts).
 *
 * Two `variant`s:
 * - `"icon"` (default) — 26px square buttons; icon-led with an accessible label
 *   (falls back to the label text when no icon is given).
 * - `"text"` — buttons sized to their TEXT (26px tall, comfortable padding) so
 *   word labels breathe instead of cramming into a square. A leading icon still
 *   renders when supplied.
 */
import { type Component, For, Show, type JSX } from "solid-js";
import { Dynamic } from "solid-js/web";
import { cn } from "./utils/cn";
import { focusRing } from "./utils/focus-ring";

type IconComponent = Component<{ size?: number | string; class?: string }>;

export function SegmentedControl<T extends string>(props: {
  items: { value: T; label: string; icon?: IconComponent }[];
  value: T;
  onChange: (value: T) => void;
  variant?: "icon" | "text";
  ariaLabel?: string;
  class?: string;
}): JSX.Element {
  const isText = (): boolean => props.variant === "text";
  return (
    <div
      role="radiogroup"
      aria-label={props.ariaLabel}
      class={cn(
        "inline-flex h-8 shrink-0 items-center gap-[2px] rounded-small border px-[2px]",
        "border-[color:var(--c9-border)]",
        props.class,
      )}
    >
      <For each={props.items}>
        {(item) => {
          const selected = (): boolean => props.value === item.value;
          return (
            <button
              type="button"
              role="radio"
              aria-checked={selected()}
              aria-label={item.label}
              title={item.label}
              onClick={() => props.onChange(item.value)}
              class={cn(
                "inline-flex items-center justify-center gap-075 rounded-xsmall border",
                "text-body font-[570] transition-colors",
                isText() ? "h-[26px] px-150" : "size-[26px]",
                selected()
                  ? "border-[color:var(--c9-border-selected)] bg-[var(--c9-bg-selected)] text-[color:var(--c9-text-selected)]"
                  : "border-transparent text-[color:var(--c9-icon-subtle)] hover:bg-[var(--c9-bg-neutral-subtle-hovered)]",
                focusRing,
              )}
            >
              <Show
                when={isText()}
                fallback={
                  <Show when={item.icon} fallback={item.label}>
                    <Dynamic component={item.icon!} size={16} />
                  </Show>
                }
              >
                <Show when={item.icon}>
                  <Dynamic component={item.icon!} size={16} />
                </Show>
                {item.label}
              </Show>
            </button>
          );
        }}
      </For>
    </div>
  );
}
